import dayjs from 'dayjs';

export type ExportParamValue = string | number | undefined | null;

// Mirrors listDeposits/listWithdrawals param building: omit undefined/null/''.
// Numbers (incl. 0) and timestamps (incl. 0) are kept and stringified.
export function buildExportParams(
  filter: Record<string, ExportParamValue>,
): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filter).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return params;
}

export function parseFilenameFromDisposition(
  disposition: string | null | undefined,
): string | null {
  if (!disposition) return null;
  const match = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  return match?.[1] ?? null;
}

// Fallback when the server omits Content-Disposition.
// e.g. relay-deposits_ALL_20260101-20260131.csv. An omitted provider or date bound is
// rendered as "ALL"; when BOTH date bounds are omitted the range collapses to a single "ALL".
export function fallbackExportFilename(
  prefix: string,
  filter: { provider?: string; timeFrom?: number; timeTo?: number },
): string {
  const provider = filter.provider && filter.provider !== '' ? filter.provider : 'ALL';
  const { timeFrom, timeTo } = filter;
  const fmt = (ms?: number) => (ms ? dayjs(ms).format('YYYYMMDD') : 'ALL');
  const range = !timeFrom && !timeTo ? 'ALL' : `${fmt(timeFrom)}-${fmt(timeTo)}`;
  return `${prefix}_${provider}_${range}.csv`;
}

function isBlobLike(value: unknown): value is Blob {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { text?: unknown }).text === 'function'
  );
}

// Axios with responseType:'blob' returns error bodies as a Blob.
// Read it and pull `message` out of the JSON envelope {code,message,data}.
export async function extractBlobErrorMessage(
  body: Blob | unknown,
): Promise<string | null> {
  if (!isBlobLike(body)) return null;
  try {
    const text = await body.text();
    const json = JSON.parse(text);
    return typeof json?.message === 'string' ? json.message : null;
  } catch {
    return null;
  }
}
